/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import humanize from 'humanize-plus';
import { DateTime } from 'luxon';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import CheckIcon from '@mui/icons-material/Check';
import {
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Switch,
  Typography,
  Zoom,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';

import { useUpdateCluster } from '../../../../../../components/hooks/useUpdateCluster';
import { SimpleConfirmModal } from '../../../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../../../../../components/SimpleErrorModal/SimpleErrorModal';
import { SimpleNotifyModal } from '../../../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../../../../../i18n';
import { ClusterApiService } from '../../../../../../services/ClusterApiService';
import { useBackupsConfigureStyles } from '../../../../../../styles/backupsConfigure';
import { useFormStyles } from '../../../../../../styles/form';
import { ClusterDetailsResponsePayload } from '../../../../../../types/cluster/ClusterDetailsResponsePayload';
import { ClusterLogicalResponsePayload } from '../../../../../../types/cluster/ClusterLogicalResponsePayload';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { formatBackupDateTimeWithTimezone } from '../../../../../../utils/formatBackupDateTimeWithTimezone';
import { fetchLogicalBackupStatus } from '../../../../../../utils/queryFns';
import { reactToCancelLogicalBackup, reactToTriggerLogicalBackup } from '../../../../../../utils/reactions';
import { useObjectState } from '../../../../../../utils/useObjectState';
import { useProjectPermissions } from '../../../../../../utils/useProjectPermissions';
import { BackupsTable } from '../BackupsTable/BackupsTable';
import { validationSchema } from './BackupsLogical.consts';
import { ConfigUpdateState } from './BackupsLogical.types';

type FormikValuesLogical = {
  enabled: boolean;
  scheduleTime: DateTime;
  daysToRetain: string;
};

type Props = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  details: ClusterDetailsResponsePayload;
  isSpinning: boolean;
  utcLocal: string;
  utcServer: string;
  logicalBackups: ClusterLogicalResponsePayload[];
  isFetchingLogicalBackups: boolean;
};

export const BackupsLogical = memo<Props>(props => {
  const { identificationData, details, isSpinning, utcLocal, utcServer, logicalBackups, isFetchingLogicalBackups } =
    props;

  const permissions = useProjectPermissions(identificationData.organizationId, identificationData.projectId);

  const classes = useBackupsConfigureStyles();
  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.Cluster);
  const [isBackupProcessing, setIsBackupProcessing] = useState(false);
  const [isOpenNotifyModal, setIsOpenNotifyModal] = useState(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);
  const [updateCluster, updateClusterResult] = useUpdateCluster();

  const [configUpdateState, setConfigUpdateState] = useObjectState<ConfigUpdateState>({
    isProcessing: false,
    isLoading: false,
    isSuccess: false,
  });

  const [isCancelBackupNowOpen, setIsCancelBackupNowOpen] = useState(false);

  // BackupSize
  const logicalBackupsSize = useMemo(
    () => logicalBackups.map(obj => obj.sizeInBytes).reduce((prev, next) => prev + next, 0),
    [logicalBackups],
  );

  const logicalBackupsFullSize = useMemo(() => humanize.fileSize(logicalBackupsSize), [logicalBackupsSize]);

  const logicalBackupStatusQuery = useQuery(
    [fetchLogicalBackupStatus.key, { ...identificationData }],
    fetchLogicalBackupStatus.fn,
  );

  const isSomethingFetching = useMemo(
    () => isFetchingLogicalBackups || logicalBackupStatusQuery.status === 'loading',
    [isFetchingLogicalBackups, logicalBackupStatusQuery.status],
  );

  const isEnableInit = useMemo<boolean>(() => {
    return details.backupConfiguration.logical.enabled;
  }, [details]);

  const schedule = useMemo<string[]>(() => {
    return details.backupConfiguration.logical.schedule.split(' ');
  }, [details]);

  const scheduleTime = useMemo<DateTime>(
    () =>
      DateTime.utc().set({
        hour: +schedule[1],
        minute: +schedule[0],
        second: 0,
        millisecond: 0,
      }),
    [schedule],
  );

  const daysToRetain = useMemo<number>(() => {
    return details.backupConfiguration.logical.daysToRetain;
  }, [details]);

  const nextBackupUtc = useMemo(() => {
    return formatBackupDateTimeWithTimezone({
      utc: true,
      dateTime: DateTime.utc(),
      schedule: { minutes: +schedule[0], hours: +schedule[1] },
    });
  }, [schedule]);

  const nextBackupLocal = useMemo(() => {
    return formatBackupDateTimeWithTimezone({
      dateTime: DateTime.local(),
      schedule: { minutes: +schedule[0], hours: +schedule[1] },
    });
  }, [schedule]);

  const initialValues = useMemo(
    () => ({
      enabled: isEnableInit,
      scheduleTime: scheduleTime,
      daysToRetain: String(daysToRetain),
    }),
    [daysToRetain, isEnableInit, scheduleTime],
  );

  const handleFormSubmit = async (values: FormikValuesLogical) => {
    const logicalData = !values.enabled
      ? {
          enabled: false,
        }
      : {
          enabled: values.enabled,
          schedule: `${values.scheduleTime.toFormat('mm')} ${values.scheduleTime.toFormat('HH')} * * *`,
          daysToRetain: Number(values.daysToRetain),
        };
    try {
      setConfigUpdateState({ ...configUpdateState, isProcessing: true, isLoading: true });
      await updateCluster({
        ...identificationData,
        data: {
          configuration: {
            backup: {
              logical: logicalData,
            },
          },
        },
      });
    } catch (e) {
      setIsOpenErrorModal(true);
      console.error(e);
    } finally {
      setTimeout(() => setConfigUpdateState({ ...configUpdateState, isProcessing: false }), 4000);
    }
  };

  const onConfirmBackupNow = useCallback(async () => {
    try {
      setIsBackupProcessing(true);
      setIsOpenNotifyModal(true);
      await ClusterApiService.createClusterLogicalBackup({
        ...identificationData,
      });
      await reactToTriggerLogicalBackup({ ...identificationData });
    } catch (e) {
      console.error(e);
      setIsOpenErrorModal(true);
    } finally {
      setIsBackupProcessing(false);
    }
  }, [setIsBackupProcessing, setIsOpenNotifyModal, setIsOpenErrorModal, identificationData]);

  const onCancelBackupNow = async () => {
    try {
      setIsCancelBackupNowOpen(false);
      setIsBackupProcessing(true);
      await ClusterApiService.cancelClusterLogicalBackupCreating({
        ...identificationData,
      });
      await reactToCancelLogicalBackup({ ...identificationData });
    } catch (e) {
      console.error(e);
      setIsOpenErrorModal(true);
    } finally {
      setIsBackupProcessing(false);
    }
  };

  const onCloseNotify = () => {
    setIsOpenNotifyModal(false);
  };

  const formik = useFormik<FormikValuesLogical>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: handleFormSubmit,
  });

  const { setFieldValue, setFieldTouched, handleSubmit, values, handleChange, errors, dirty } = formik;

  const handleTimeChange = (value: DateTime | null) => {
    if (!value) {
      void setFieldValue('scheduleTime', null);
    } else {
      void setFieldValue('scheduleTime', value);
    }
    void setFieldTouched('scheduleTime', true, false);
  };

  const isFetching = useMemo(
    () => isSpinning || isBackupProcessing || configUpdateState.isProcessing || isSomethingFetching,
    [isSpinning, isBackupProcessing, configUpdateState.isProcessing, isSomethingFetching],
  );

  const createBackupButton = useMemo(
    () => (
      <div style={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
        <Button
          data-cy="cluster-backups-tab-create-backup-button"
          disabled={isFetching}
          variant="contained"
          className={classes.backupNowButton}
          onClick={onConfirmBackupNow}
        >
          {t('details.backups.backup_now')}
        </Button>
      </div>
    ),
    [isFetching, classes, onConfirmBackupNow, t],
  );

  useEffect(() => {
    if (updateClusterResult.isSuccess) {
      setTimeout(() => setConfigUpdateState({ ...configUpdateState, isLoading: false, isSuccess: true }), 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateClusterResult.isSuccess]);

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit}>
        <Grid item classes={{ root: 'd-flex' }}>
          <div style={{ width: '100%' }}>
            <Grid item classes={{ root: 'd-flex' }}>
              <Switch
                name="enabled"
                checked={values.enabled}
                onChange={handleChange}
                disabled={!permissions.edit_cluster || isFetching}
                aria-disabled={!permissions.edit_cluster || isFetching}
                data-cy="cluster-page-enable-logical-backup"
                sx={{ mr: '20px' }}
              />
              <Typography variant={'h3'} classes={{ root: classes.heading }}>
                {t('details.backups.backup_schedule')}
              </Typography>
            </Grid>
            <Grid item classes={{ root: 'd-flex' }} sx={{ mt: '16px', alignItems: 'center' }}>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}
                variant="outlined"
              >
                <TimePicker
                  label={t('details.backups.backups_time')}
                  value={values.scheduleTime}
                  ampm={false}
                  timezone="UTC"
                  onChange={value => handleTimeChange(value)}
                  disabled={!values.enabled || isFetching}
                  readOnly={!permissions.edit_cluster}
                />
              </FormControl>
              <FormControl
                className={cx(classes.formControl, formClasses.formControl, formClasses.widthMedium)}
                error={!!errors.daysToRetain}
                variant="outlined"
              >
                <InputLabel htmlFor="daysToRetain">{t('details.backups.retention_period')}</InputLabel>
                <OutlinedInput
                  label={t('details.backups.retention_period')}
                  name="daysToRetain"
                  error={!!errors.daysToRetain}
                  fullWidth
                  type="text"
                  value={values.daysToRetain}
                  endAdornment={<InputAdornment position="start">{t('details.backups.days')}</InputAdornment>}
                  onChange={handleChange}
                  disabled={!values.enabled || isFetching}
                  readOnly={!permissions.edit_cluster}
                />
                {!!errors.daysToRetain && (
                  <FormHelperText>{t('details.backups.retention_period_not_valid')}</FormHelperText>
                )}
              </FormControl>
              <Fade in={dirty || configUpdateState.isProcessing}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!permissions.edit_cluster || isFetching}
                  data-cy="cluster-page-logical-backup-apply-button"
                >
                  {configUpdateState.isSuccess ? (
                    <Zoom in>
                      <CheckIcon color="primary" sx={{ fontSize: '24px' }} />
                    </Zoom>
                  ) : configUpdateState.isLoading ? (
                    <Zoom in>
                      <CircularProgress size={24} />
                    </Zoom>
                  ) : (
                    t('apply')
                  )}
                </Button>
              </Fade>
            </Grid>
            <Collapse in={values.enabled}>
              <Divider component="div" sx={{ mt: '16px', mb: '16px', maxWidth: '595px' }} />
              <div>
                <Typography variant="h3" classes={{ root: classes.h3 }}>
                  {t('details.backups.next_backup_scheduled')}
                </Typography>
                <div className={classes.timeContainer}>
                  <div style={{ marginRight: '3px' }}>
                    <Typography className={classes.timeLabel}>{t('details.backups.server_time')}:</Typography>
                    <Typography className={classes.timeLabel}>{t('details.backups.your_local_time')}:</Typography>
                  </div>
                  <div>
                    <Typography className={classes.timeLabel}>{nextBackupUtc}</Typography>
                    <Typography className={classes.timeLabel}>{nextBackupLocal}</Typography>
                  </div>
                </div>
                <Typography className={cx(classes.restoreSize, classes.timeLabel)}>
                  {t('details.backups.backup_size', {
                    currentSize: logicalBackupsFullSize,
                  })}
                </Typography>
              </div>
            </Collapse>
          </div>
        </Grid>
      </form>
      <Divider component="div" sx={{ mt: '16px', mb: '16px', maxWidth: '595px' }} />
      <BackupsTable
        identificationData={identificationData}
        logicalBackups={logicalBackups}
        utcLocal={utcLocal}
        utcServer={utcServer}
        createBackupButton={createBackupButton}
      />
      <SimpleNotifyModal
        open={isOpenNotifyModal}
        message={t('details.backups.backup_now_in_progress')}
        onOk={onCloseNotify}
      />
      <SimpleConfirmModal
        open={isCancelBackupNowOpen}
        message={t('details.backups.want_to_cancel_backup_now')!}
        onCancel={() => setIsCancelBackupNowOpen(false)}
        onConfirm={onCancelBackupNow}
      />
      <SimpleErrorModal open={isOpenErrorModal} onOk={() => setIsOpenErrorModal(false)} />
    </div>
  );
});
